import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    SyncNotificationSubscriptionDocument,
    SyncNotificationSubscriptionMutationVariables,
    SyncNotificationSubscriptionMutationResult,
    SubscribePushMutationResult,
    SubscribePushMutationVariables,
    SubscribePushDocument,
    UnsubscribePushMutationResult,
    UnsubscribePushMutationVariables,
    UnsubscribePushDocument,
    GetSubscribePushSettingsQueryResult,
    GetSubscribePushSettingsQueryVariables,
    GetSubscribePushSettingsDocument,
    PushSubscriptionInput
} from "~/queries";
import {ApolloClient, useApolloClient} from "@apollo/client";
import {tuple} from "~/types";
import {BroadcastAndLocalChannel} from "~/notifications/BroadcastAndLocalChannel";


const channel = new BroadcastAndLocalChannel("push-subscription");


function createPushSubscriptionInput(subscription?: PushSubscription): PushSubscriptionInput {
    if (!subscription) {
        return;
    }
    const data = subscription.toJSON();
    return {
        authKey: data.keys.auth,
        p256dhKey: data.keys.p256dh,
        endpoint: data.endpoint,
        userAgent: window?.navigator?.userAgent,
    }
}


async function getPushManager(): Promise<PushManager> {
    const registration = await Promise.resolve(window?.navigator?.serviceWorker?.getRegistration());
    return registration?.pushManager;
}

async function getPushSubscription() {
    const pushManager = await getPushManager();
    return await Promise.resolve(pushManager?.getSubscription());
}

async function getPushSubscriptionOptionsInit(client: ApolloClient<any>): Promise<PushSubscriptionOptionsInit | null> {
    const {data} = await client.query<GetSubscribePushSettingsQueryResult, GetSubscribePushSettingsQueryVariables>({
        query: GetSubscribePushSettingsDocument,
        notifyOnNetworkStatusChange: true
    });
    return {
        userVisibleOnly: true,
        applicationServerKey: data?.subscribePushSettings?.serverKey
    }
}

async function syncPushSubscription(client: ApolloClient<any>): Promise<PushSubscription | null> {
    const subscription = await getPushSubscription();
    const {data} = await client.mutate<SyncNotificationSubscriptionMutationResult, SyncNotificationSubscriptionMutationVariables>({
        mutation: SyncNotificationSubscriptionDocument,
        variables: {
            subscription: createPushSubscriptionInput(subscription)
        }
    });
    if (data.syncPushSubscription.remove && subscription) {
        await subscription.unsubscribe();
        return;
    }
    return subscription;
}

async function getPushPermissionState(client: ApolloClient<any>): Promise<"denied" | "granted" | "prompt"> {
    const options = await getPushSubscriptionOptionsInit(client);
    const registration = await Promise.resolve(window?.navigator?.serviceWorker?.getRegistration());
    return await Promise.resolve(registration?.pushManager?.permissionState(options));
}

async function subscribe(client: ApolloClient<any>) {
    const manager = await getPushManager();
    if (!manager) {
        return;
    }
    const options = await getPushSubscriptionOptionsInit(client);
    const subscription = await manager.subscribe(options);

    try {
        await client.mutate<SubscribePushMutationResult, SubscribePushMutationVariables>({
            mutation: SubscribePushDocument,
            variables: {
                subscription: createPushSubscriptionInput(subscription)
            }
        });
        channel?.postMessage("changed");
    } catch (e) {
        await subscription.unsubscribe();
        throw e;
    }
}

async function unsubscribe(client: ApolloClient<any>) {
    const manager = await getPushManager();
    if (!manager) {
        return;
    }
    const subscription = await getPushSubscription();
    if (!subscription) {
        return;
    }
    await client.mutate<UnsubscribePushMutationResult, UnsubscribePushMutationVariables>({
        mutation: UnsubscribePushDocument,
        variables: {
            subscription: createPushSubscriptionInput(subscription)
        }
    });
    await subscription.unsubscribe();
    channel?.postMessage("changed");
}

export const useSyncPushSubscription = () => {
    const client = useApolloClient();
    const [state, setState] = useState<{ synchronized: boolean, error?: Error }>({synchronized: false});

    const sync = useCallback(() => {
        syncPushSubscription(client).then(() => {
            setState({synchronized: true});
        }).catch(error => {
            setState({error: error, synchronized: false});
        });
    }, [client, setState])

    return tuple(sync, state);
}

export const usePushSubscription = () => {
    const client = useApolloClient();
    const mutexInitialValue = useMemo(() => Promise.resolve(), []);
    const $mutex = useRef<Promise<any>>(mutexInitialValue);
    const [state, setState] = useState<{ subscription?: PushSubscription, loading: boolean, error?: Error }>({loading: true});

    const refresh = useCallback((sync: boolean = false) => {
        $mutex.current = $mutex.current.then(async () => {
            try {
                if (sync) {
                    await syncPushSubscription(client);
                }
                const subscription = await getPushSubscription();
                setState({
                    subscription: subscription,
                    loading: false,
                });
            } catch (e) {
                setState(state => ({...state, error: e, loading: false}));
            }
        });
    }, [client])

    useEffect(() => {
        const listener = () => refresh();
        refresh(true);
        channel?.addEventListener("message", listener);
        return () => channel?.removeEventListener("message", listener);
    }, [refresh])
    return state;
}


export const useSubscribePush = () => {
    const client = useApolloClient();
    const [state, setState] = useState<{loading: boolean, error?: Error}>({loading: false});

    const execute = useCallback(async () => {
        setState({loading: true});
        try {
            await subscribe(client);
            setState({loading: false});
        } catch (e) {
            setState({loading: false, error: e});
            throw e
        }
    }, [client])

    return tuple(execute, state);
}


export const useUnsubscribePush = () => {
    const client = useApolloClient();
    const [state, setState] = useState<{loading: boolean, error?: Error}>({loading: false});

    const execute = useCallback(async () => {
        setState({loading: true});
        try {
            await unsubscribe(client);
            setState({loading: false});
        } catch (e) {
            setState({loading: false, error: e});
            throw e
        }
    }, [client])

    return tuple(execute, state);
}